/**
 * Application-wide constants
 */

export const QUERY_CONFIG = {
  STALE_TIME: 30 * 60 * 1000, // 30 minutes
  GC_TIME: 60 * 60 * 1000, // 1 hour
  RETRY: 1,
  REFETCH_ON_WINDOW_FOCUS: false,
  REFETCH_ON_RECONNECT: false,
} as const;

/**
 * Default currency symbol — used as fallback before a store/region is selected.
 * Runtime currency is determined by the selected store's region via
 * useStoreSelectionStore.currencySymbol (ROI → €, UK → £).
 */
export const CURRENCY_SYMBOL = "£"; // default UK; overridden per-region at runtime

export const STORAGE_KEYS = {
  CART: "cart-storage",
  AUTH: "auth-storage",
  STORE_SELECTION: "store-selection-storage",
  CHICKEN_DIP_DEAL: "chicken-dip-deal-selections",
} as const;

// ── Public Access Domains ──
// Traffic from these domains bypasses the login wall entirely.
// The site behaves as a public customer-facing app (no authentication required).
//
// SECURITY: Only genuine production domains are listed here.
// - pizzaapp-{env}.2sdtechnologies.com → requires named user login
// - localhost → requires named user login (prevents domain spoofing bypass)
// - Backend also validates Host header to prevent spoofing
export const PUBLIC_DOMAINS = [
  "dominos.co.uk",
  "dominos.ie",
  "www.dominos.co.uk",
  "www.dominos.ie",
] as const;

/**
 * Check if the current hostname is a public-access domain.
 * Returns true ONLY for genuine Domino's production domains — no login required.
 * All other domains require named user authentication.
 */
export const isPublicDomain = (): boolean => {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname.toLowerCase();
  return PUBLIC_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
  );
};

/**
 * Detect region from hostname for UK/Ireland behavior.
 * - dominos.ie → "ROI" (Republic of Ireland: EUR, Eircode)
 * - dominos.co.uk / everything else → "UK" (GBP, UK postcode)
 */
export const getRegionFromHostname = (): "UK" | "ROI" => {
  if (typeof window === "undefined") return "UK";
  const hostname = window.location.hostname.toLowerCase();
  if (hostname.includes("dominos.ie")) return "ROI";
  return "UK";
};

/**
 * Google Pay token types to request: PAN_ONLY only.
 *
 * PAN_ONLY is the card number stored in the customer's Google account, with no
 * cryptogram. PSD2 requires SCA for it in both UK and ROI, so checkout runs 3DS
 * on the Google Pay token before every charge.
 *
 * CRYPTOGRAM_3DS (a device token from a tap-to-pay card in Google Wallet on
 * Android) is deliberately not requested: we take Google Pay on the web only.
 * This list is used as-is, whatever allowedCardAuthMethods the backend sends.
 */
export const GOOGLE_PAY_AUTH_METHODS: readonly string[] = ["PAN_ONLY"];
